import React, { useState } from 'react';
import { CheckCircle, MoreHorizontal, Trash2, UserMinus } from 'lucide-react';
import PanelSheet from '../../components/PanelSheet';
import { Circle, CircleDetails, Member, roleLabel } from './model';
import { CircleAvatar, circleDate } from './CircleList';
export default function CircleDetail({
  summary,
  detail,
  loading,
  error,
  retry,
  close,
  remove,
  deleteCircle
}: {
  summary: Circle;
  detail: CircleDetails | null;
  loading: boolean;
  error: string;
  retry: () => void;
  close: () => void;
  remove: (member: Member) => void;
  deleteCircle: () => void;
}) {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const members = (detail?.members || []).filter(member => [member.id, member.email, member.username, member.full_name].join(' ').toLowerCase().includes(query.toLowerCase()) && (!role || member.role === role));
  return <PanelSheet
    title="Çevre detayları"
    close={close}
    closeLabel="Çevre detaylarını kapat"
    wide
    footer={<div className="flex justify-between items-center gap-3">
    <span className="text-xs text-gray-500">
      Çevre #
      {summary.id}
    </span>
    <details className="relative">
      <summary className="user-btn cursor-pointer list-none">
        <MoreHorizontal size={16} />
        Yönet
      </summary>
      <div className="management-menu absolute right-0 bottom-full mb-2 bg-white rounded-xl border border-gray-200 shadow-lg p-2 min-w-[200px]">
        <button className="min-h-[44px] flex gap-2 items-center text-red-600 px-3 text-sm" onClick={deleteCircle}>
          <Trash2 size={16} />
          Çevreyi sil
        </button>
      </div>
    </details>
  </div>}
  >
    <div className="flex gap-3 items-center">
      <CircleAvatar name={summary.name} url={summary.avatar_url} />
      <div className="min-w-0">
        <h3 className="font-semibold text-xl break-words">
          {detail?.name || summary.name}
        </h3>
        <p className="text-xs text-gray-500">
          {`${detail?.member_count ?? summary.member_count} üye · #${summary.id}`}
        </p>
      </div>
    </div>
    <p className="mt-4 text-sm text-gray-500 break-words">
      {detail?.description ?? summary.description ?? 'Açıklama yok'}
    </p>
    <dl className="grid grid-cols-2 gap-4 mt-6 text-sm">
      <div>
        <dt className="user-label">Sahip</dt>
        <dd className="break-words">
          {summary.owner_name || 'Sahip bilinmiyor'}
          <span className="block text-xs text-gray-400">
            Kullanıcı #
            {detail?.owner_id ?? summary.owner_id}
          </span>
        </dd>
      </div>
      <div>
        <dt className="user-label">Oluşturulma</dt>
        <dd>
          {circleDate(detail?.created_at || summary.created_at)}
        </dd>
      </div>
    </dl>
    <section className="border-t border-gray-100 mt-6 pt-5">
      <h3 className="font-semibold">
        Üyeler{' '}
        <span className="text-gray-400 text-sm font-normal">
          {detail ? `${members.length} gösterilen / ${detail.member_count}` : ''}
        </span>
      </h3>
      {loading && <p role="status" className="text-sm text-gray-500 py-6">Üyeler yükleniyor…</p>}
      {error && <p role="alert" className="text-sm text-red-600 py-4">
        {error}
        <button className="underline ml-2" onClick={retry}>Yeniden dene</button>
      </p>}
      {detail && <><div className="mt-4 space-y-3">
          <input className="management-search user-input" aria-label="Üyelerde ara" placeholder="İsim, e-posta veya kullanıcı kimliği ara…" value={query} onChange={event => setQuery(event.target.value)} />
          <label className="user-label" htmlFor="circle-member-role">Üye rolü</label>
          <select id="circle-member-role" className="user-input" value={role} onChange={event => setRole(event.target.value)}>
            <option value="">Tüm roller</option>
            {Array.from(new Set(detail.members.map(member => member.role))).map(value => <option key={value} value={value}>
              {roleLabel(value)}
            </option>)}
          </select>
        </div>
        <div className="divide-y divide-gray-100 mt-4">
          {members.map(member => <article key={member.id} className="py-4">
            <div className="flex gap-3 items-start">
              <CircleAvatar name={member.full_name || member.username} url={member.avatar_url} />
              <div className="min-w-0 flex-1">
                <h4 className="font-medium text-sm break-words">
                  {member.full_name || member.username}
                  {member.is_verified && <CheckCircle size={14} className="inline ml-1 text-blue-600" aria-label="Doğrulanmış" />}
                </h4>
                <p className="text-xs text-gray-500 break-all">
                  {member.email}
                </p>
                <p className="text-xs text-gray-400 break-words mt-1">
                  {`@${member.username} · Kullanıcı #${member.id}`}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mt-3">
              <span className="text-xs text-lifeshare-700 bg-lifeshare-50 rounded-lg px-2 py-1.5">
                {roleLabel(member.role)}
              </span>
              <span className="text-xs text-gray-500 text-right">
                {`Katılım: ${circleDate(member.joined_at)}`}
              </span>
            </div>
            {member.id !== detail.owner_id && <button onClick={() => remove(member)} className="user-btn text-red-600 mt-3 w-full">
              <UserMinus size={16} />
              Üyeyi çıkar
            </button>}
          </article>)}
        </div>
        {!members.length && <p className="text-sm text-gray-500 py-8">Aramanızla eşleşen üye yok.</p>}
      </>}
    </section>
  </PanelSheet>;
}
